import { z } from 'zod';

/**
 * Media attachment shapes. `parentType` is free-text so new consumer
 * modules can attach without a schema change, though the API layer
 * should still verify the caller has permission for the parent row.
 */

export const MediaParentType = z.enum([
  'crm_contact',
  'transaction',
  'seller_property',
  'ad_hoc',
  'mail_draft',
  // Inline images embedded in a user-scoped email signature. The
  // image lives in @yrp/storage + the signature HTML references the
  // storage URL via <img src>, so the signature is portable across
  // recipients + doesn't bloat every outbound with a base64 blob.
  'email_signature',
  // A file an agent attached to an outbound SMS/MMS from the comms
  // composer, or a picture a client texted in. Bound to the recipient's
  // crm_contact (parentId). Twilio fetches it by storage key from the
  // unauthenticated /media/public/sms-media route (the key is the
  // secret); by id it is served only to the conversation's agent.
  'sms_attachment',
  // The same thing, for a thread that has no CRM contact behind it —
  // an unmatched number the agent is texting directly. parentId = the
  // sending agent's user id (the `report` precedent below), because
  // there is no contact row to hang it on and reusing `sms_attachment`
  // would put user ids and contact ids in one namespace, where
  // contact 7 and user 7 become indistinguishable in the storage path.
  //
  // Being unmatched changes nothing about where the bytes end up: the
  // file is still fetched by Twilio and still delivered to a phone, so
  // it carries the same public posture as `sms_attachment`.
  'sms_attachment_unmatched',
  // A server-rendered document from POST /reports/render (offer
  // summary, GFE, CMA, letter). parentId = the rendering agent's user
  // id — reports belong to the agent, not to a single entity row.
  'report',
  // A LOCAL file an agent staged through POST /media/upload-url (a scan,
  // a PDF made in a sandbox) on its way to a Transact file or an email.
  // parentId = the uploading user's id. OWNER-ONLY: the content, list and
  // delete routes refuse every other caller (MEDIA_PARENT_ACCESS 'self'),
  // because these are contract documents and media ids are sequential.
  'staged_upload',
]);
export type MediaParentType = z.infer<typeof MediaParentType>;

/**
 * Whose file is it? Media ids are sequential, so "who may reach this row"
 * cannot be left to whether someone knows the id. Every parent type answers
 * it here, exactly once, and apps/api/src/features/media/access.ts enforces
 * the answer on the authenticated media routes:
 *
 *  - `uploader` — only the user who uploaded it. The parent id carries no
 *    authority: an email attachment's parent is a timestamp or a draft id.
 *  - `self` — the parent id IS the user (a rendered report, a staged
 *    upload). Only that user, who is also the uploader.
 *  - `transaction` — the transaction's agent.
 *  - `contact` — the contact's owner, or a delegate working the owner's
 *    contact book: the same people who may edit the contact's notes,
 *    phones and addresses.
 *  - `seller_property` — the seller's agent.
 *  - `sms_contact` — a picture on a 1:1 text thread: whoever uploaded it,
 *    the contact's owner, or a delegate the owner released that contact's
 *    conversation to (read only).
 *  - `sms_unmatched` — a picture on a 1:1 thread with no contact: the
 *    parent id is the agent.
 *
 * A Record, so a new parent type that does not say whose it is fails to
 * compile; a stored parent type missing from the enum (the worker's
 * `group_sms_attachment`) is never served by id at all.
 */
export type MediaParentAccess =
  | 'uploader'
  | 'self'
  | 'transaction'
  | 'contact'
  | 'seller_property'
  | 'sms_contact'
  | 'sms_unmatched';

export const MEDIA_PARENT_ACCESS: Readonly<Record<MediaParentType, MediaParentAccess>> = {
  crm_contact: 'contact',
  transaction: 'transaction',
  seller_property: 'seller_property',
  ad_hoc: 'uploader',
  mail_draft: 'uploader',
  email_signature: 'uploader',
  sms_attachment: 'sms_contact',
  sms_attachment_unmatched: 'sms_unmatched',
  report: 'self',
  staged_upload: 'self',
};

/** The access rule for a stored parent type; null when it has none (never served by id). */
export function mediaParentAccess(parentType: string): MediaParentAccess | null {
  const parsed = MediaParentType.safeParse(parentType);
  return parsed.success ? MEDIA_PARENT_ACCESS[parsed.data] : null;
}

export const MediaAttachment = z.object({
  id: z.number().int().positive(),
  parentType: z.string(),
  parentId: z.number().int().positive(),
  storageKey: z.string(),
  storageUrl: z.string(),
  fileName: z.string(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  uploadedByUserId: z.number().int().positive().nullable(),
  createdAt: z.string().datetime({ offset: true }),
});
export type MediaAttachment = z.infer<typeof MediaAttachment>;

export const MediaUploadResponse = z.object({
  attachment: MediaAttachment,
});
export type MediaUploadResponse = z.infer<typeof MediaUploadResponse>;

/**
 * A listed attachment: everything but where the bytes live. The storage URL
 * is a presigned bearer link (seven days on Spaces) and the key names the
 * object, so neither leaves the API in a listing — a list is read by people
 * and AI sessions who need only the id, which `/api/media/:id/content`
 * serves to whoever may read the file. The upload response still carries
 * both: the uploader's own clients build MMS URLs from the key.
 */
export const MediaAttachmentSummary = MediaAttachment.omit({ storageKey: true, storageUrl: true });
export type MediaAttachmentSummary = z.infer<typeof MediaAttachmentSummary>;

export const MediaListResponse = z.object({
  attachments: z.array(MediaAttachmentSummary),
});
export type MediaListResponse = z.infer<typeof MediaListResponse>;

/**
 * Upload cap for a file staged through `POST /media/upload-url`. Lone Wolf
 * refuses uploads over 25 MB and so do most recipients' mail servers, so a
 * larger file could not reach either destination anyway. It also bounds
 * what an unauthenticated PUT can make the API buffer.
 */
export const MEDIA_UPLOAD_URL_MAX_BYTES = 25 * 1024 * 1024;

/**
 * `POST /media/upload-url` — mint a short-lived PUT URL for a LOCAL file.
 *
 * This is the byte path that does not go through SharePoint. The PUT stores
 * the file in YRP storage as one of the caller's own `staged_upload` rows
 * (owner-only) and answers with its media id. That id is accepted by
 * `transact_upload_document` (mediaId), by the mail draft tools
 * (attachmentIds) and by the draft attach route (mediaId), so a scan or a
 * generated PDF can reach Lone Wolf or an email with no drive write and no
 * base64 in a model's context.
 */
export const MediaUploadUrlRequest = z
  .object({
    /** Name the stored file keeps, extension included. */
    fileName: z.string().trim().min(1).max(255),
  })
  .strict();
export type MediaUploadUrlRequest = z.infer<typeof MediaUploadUrlRequest>;

export const MediaUploadUrlResponse = z.object({
  /** PUT the raw file bytes here. No Authorization header: the URL is the capability. */
  uploadUrl: z.string().url(),
  expiresAt: z.string().datetime({ offset: true }),
  maxBytes: z.number().int().positive(),
  fileName: z.string(),
});
export type MediaUploadUrlResponse = z.infer<typeof MediaUploadUrlResponse>;

/**
 * What the PUT answers. Deliberately not the full MediaAttachment: the
 * storage key and URL stay server-side (a presigned URL in an agent's
 * transcript is a bearer link to a contract), and the id is all any caller
 * needs.
 */
export const MediaStagedUploadResponse = z.object({
  mediaId: z.number().int().positive(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
});
export type MediaStagedUploadResponse = z.infer<typeof MediaStagedUploadResponse>;

/** An owned transaction document may provide its own retained display name. */
export const MediaContentQuery = z.object({
  documentId: z.coerce.number().int().positive().optional(),
});
export type MediaContentQuery = z.infer<typeof MediaContentQuery>;

/** Only durable local paths identify media; a matching path on another host does not. */
export function transactionDocumentMediaId(filePath: string | null | undefined): number | null {
  const match = filePath?.match(/^(?:\/api)?\/media\/([1-9]\d*)\/content(?:[?#].*)?$/);
  const id = match?.[1] ? Number(match[1]) : null;
  return id !== null && Number.isSafeInteger(id) ? id : null;
}

/**
 * Outbound MMS media limits and the URL shape Twilio fetches.
 *
 * These numbers are Twilio's, not ours, and they are stricter than they look:
 *
 *  - Twilio accepts 5 MB total per message (body + all media) and rejects the
 *    whole send with error 11751 above it.
 *  - Only jpeg / png / gif are FULLY supported: Twilio resizes those for the
 *    destination carrier and "will not transcode or resize any other types of
 *    media". Everything else is capped at 500 KB per file and arrives however
 *    the recipient's handset chooses to cope.
 *  - HEIC is in Twilio's accepted-MIME table, which is a trap. A default
 *    iPhone photo is HEIC and 1.5–3 MB, so it blows the 500 KB non-image cap,
 *    is never transcoded, and commonly renders as nothing at all on Android.
 *    Clients MUST re-encode to JPEG before upload — that is the whole reason
 *    the mobile picker runs every image through the manipulator, including
 *    ones that are already small.
 *  - Carriers, not Twilio, are the real ceiling: Twilio recommends images stay
 *    under 600 KB for reliable US delivery, and AT&T/T-Mobile/Verizon publish
 *    per-file ceilings between 600 KB and 1.5 MB depending on number type.
 *
 * Sources: twilio.com/docs/messaging/api/message-resource,
 * /docs/api/errors/11751, /docs/messaging/guides/accepted-mime-types, and
 * help.twilio.com article 223133547 (auto-resize behaviour).
 */
export const SMS_MEDIA_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'] as const;
export type SmsMediaMimeType = (typeof SMS_MEDIA_MIME_TYPES)[number];

/** Hard ceiling. Twilio rejects the entire message above this (error 11751). */
export const SMS_MEDIA_MAX_BYTES = 5 * 1024 * 1024;

/** What Twilio recommends for reliable US carrier delivery. Clients target this. */
export const SMS_MEDIA_RECOMMENDED_BYTES = 600 * 1024;

/** Twilio caps media items per outbound message at ten. */
export const SMS_MEDIA_MAX_ITEMS = 10;

export function isSmsMediaMimeType(value: string | null | undefined): value is SmsMediaMimeType {
  if (!value) return false;
  const normalized = value.split(';')[0]?.trim().toLowerCase() ?? '';
  return (SMS_MEDIA_MIME_TYPES as readonly string[]).includes(normalized);
}

/** The two parent types whose bytes are fetched by Twilio and sent to a phone. */
export function isSmsAttachmentParentType(value: string): boolean {
  return value === 'sms_attachment' || value === 'sms_attachment_unmatched';
}

/**
 * Path of the unauthenticated route Twilio fetches outbound MMS media from.
 *
 * Addressed by STORAGE KEY, never by id. The id-addressed route is sequential
 * and was therefore enumerable from the open internet; the storage key carries
 * ~96 bits of entropy, which is the capability-URL posture the sibling routes
 * already use.
 *
 * Only the PATH lives here. Each client prepends its own API origin, because
 * "what is the API's base URL" genuinely differs per platform (Vite env var in
 * the browser, Expo config extra on the phone) while the route shape must not.
 */
export function smsMediaPublicPath(storageKey: string): string {
  return `/api/media/public/sms-media?key=${encodeURIComponent(storageKey)}`;
}
